import React, { useState, useEffect } from "react";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import Header from "../../Common/Layout/Header";
import MasterTabs from "../AllTabs/MasterTabs";
import { useAuth } from "../../Common/Configurations/AuthContext";
import currencyFlags from "../../Data/CurrencyFlags.json"; // Import currency flags JSON
import { useNavigate } from "react-router-dom";

const CompanyProfile = () => {
  const { user, loading, refreshUserProfile } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState({});
  const [editedCompany, setEditedCompany] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  // Process countries data from currencyFlags.json and extract unique currencies
  useEffect(() => {
    if (currencyFlags && Array.isArray(currencyFlags)) {
      const sortedCountries = [...currencyFlags].sort((a, b) =>
        a.country.localeCompare(b.country)
      );
      setCountries(sortedCountries);

      // Extract unique currencies
      const uniqueCurrencies = [];
      const currencyMap = new Map();

      sortedCountries.forEach((country) => {
        if (country.code && !currencyMap.has(country.code)) {
          currencyMap.set(country.code, true);
          uniqueCurrencies.push({
            code: country.code,
            name: country.currency || "",
          });
        }
      });

      // Sort currencies alphabetically by code
      uniqueCurrencies.sort((a, b) => a.code.localeCompare(b.code));
      setCurrencies(uniqueCurrencies);
    }
  }, []);

  // Helper function to display data (show empty for null/undefined/empty values)
  const displayValue = (value) => {
    return value && value !== "null" && value !== "undefined" ? value : "";
  };

  // Helper function to get country name from country code
  const getCountryName = (countryCode) => {
    if (!countryCode || countryCode === "null" || countryCode === "undefined")
      return "";
    const country = countries.find((c) => c.countryCode === countryCode);
    return country
      ? `${country.country} - ${country.countryCode}`
      : countryCode;
  };

  // Check if country is India
  const isIndia = (countryCode) => {
    return countryCode === "IN";
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
    }
  };

  const handleCountryChange = (countryCode) => {
    const selectedCountry = countries.find(
      (country) => country.countryCode === countryCode
    );
    setEditedCompany({
      ...editedCompany,
      country: countryCode,
      currency: selectedCountry?.code || "",
    });
  };

  const handleCurrencyChange = (currencyCode) => {
    setEditedCompany({
      ...editedCompany,
      currency: currencyCode,
    });
  };

  const handleUpdate = async () => {
    setBtnLoading(true);

    const formData = new FormData();
    formData.append("companyId", user?.cid);
    formData.append("companyName", editedCompany.companyName || "");
    formData.append("brandName", editedCompany.brandName || "");
    formData.append("email", editedCompany.email || "");
    formData.append("phoneNumber", editedCompany.phoneNumber || "");
    formData.append("gstNo", editedCompany.gstNo || "");
    formData.append("panCard", editedCompany.panCard || "");
    formData.append("address", editedCompany.address || "");
    formData.append("city", editedCompany.city || "");
    formData.append("state", editedCompany.state || "");
    formData.append("country", editedCompany.country || "");
    formData.append("currency", editedCompany.currency || "");
    formData.append("isActive", editedCompany.isActive);
    formData.append(
      "planType",
      editedCompany.planType || company.planType || ""
    );

    if (logoFile) {
      formData.append("logoFile", logoFile);
    }

    for (let pair of formData.entries()) {
      console.log(pair[0] + ": ", pair[1]);
    }

    try {
      await apiService.update(config.UPDATECOMPANY, formData);

      // Refresh user data in AuthContext after successful update
      await refreshUserProfile();

      setEditMode(false);
      setEditedCompany({});
      setLogoFile(null);
      setLogoPreview(null);
      GetData(); // Refresh local company data
    } catch (err) {
      if (err.response && err.response.data && err.response.data.errors) {
        console.error("Validation Errors:", err.response.data.errors);
      }
    } finally {
      setBtnLoading(false);
    }
  };

  const startEdit = () => {
    setEditedCompany({ ...company });
    setEditMode(true);
    setLogoPreview(company.logoUrl);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditedCompany({});
    setLogoFile(null);
    setLogoPreview(null);
  };

  const GetData = () => {
    apiService
      .getAll(config.GETCOMPANYBYUSER + "/" + user.id)
      .then((res) => {
        // Clean up null values from backend response
        const cleanedData = {};
        Object.keys(res).forEach((key) => {
          if (
            res[key] === null ||
            res[key] === "null" ||
            res[key] === "undefined"
          ) {
            cleanedData[key] = "";
          } else {
            cleanedData[key] = res[key];
          }
        });
        setCompany(cleanedData);
        setEditedCompany((prev) => ({
          ...prev,
          isActive: cleanedData.isActive,
        }));
      })
      .catch((err) => console.error("Error fetching company data", err));
  };

  useEffect(() => {
    if (user && !loading) {
      GetData();
    }
  }, [user, loading]);

  if (loading) return <p>Loading...</p>;

  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="col-xl-12">
              <div className="card">
                <div className="p-2 align-items-center">
                  <MasterTabs pageName="COMPANYPROFILE" />

                  {/* Company Logo */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">
                      Company Logo:
                    </label>
                    <div className="col-sm-4">
                      <img
                        src={editMode ? logoPreview : company.logoUrl}
                        alt="Company Logo"
                        className="img-thumbnail"
                        style={{
                          width: "150px",
                          height: "150px",
                          objectFit: "contain",
                        }}
                        onError={(e) => {
                          e.target.style.display = "none";
                        }}
                      />
                      {editMode && (
                        <div className="mt-2">
                          <input
                            type="file"
                            className="form-control"
                            accept="image/png, image/jpeg, image/gif"
                            onChange={handleFileChange}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Company Name */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">
                      Company Name:
                    </label>
                    <div className="col-sm-4">
                      {editMode ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editedCompany.companyName || ""}
                          maxLength={40}
                          onChange={(e) =>
                            setEditedCompany({
                              ...editedCompany,
                              companyName: e.target.value.toUpperCase(),
                            })
                          }
                        />
                      ) : (
                        <span className="form-control-plaintext">
                          {displayValue(company.companyName)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Brand Name */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">
                      Brand Name:
                    </label>
                    <div className="col-sm-4">
                      {editMode ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editedCompany.brandName || ""}
                          maxLength={40}
                          onChange={(e) =>
                            setEditedCompany({
                              ...editedCompany,
                              brandName: e.target.value.toUpperCase(),
                            })
                          }
                        />
                      ) : (
                        <span className="form-control-plaintext">
                          {displayValue(company.brandName)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Email:</label>
                    <div className="col-sm-4">
                      {editMode ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editedCompany.email || ""}
                          maxLength={40}
                          onChange={(e) =>
                            setEditedCompany({
                              ...editedCompany,
                              email: e.target.value,
                            })
                          }
                        />
                      ) : (
                        <span className="form-control-plaintext">
                          {displayValue(company.email)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Phone */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Phone:</label>
                    <div className="col-sm-4">
                      {editMode ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editedCompany.phoneNumber || ""}
                          maxLength={15}
                          onChange={(e) =>
                            setEditedCompany({
                              ...editedCompany,
                              phoneNumber: e.target.value,
                            })
                          }
                        />
                      ) : (
                        <span className="form-control-plaintext">
                          {displayValue(company.phoneNumber)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* GST No / Tax No - Conditionally rendered based on country */}
                  {/* GST No / Tax No - Conditionally rendered based on country */}
                  {(editMode || !editMode) && (
                    <div className="row mb-3">
                      <label className="col-sm-2 col-form-label">
                        {isIndia(
                          editMode ? editedCompany.country : company.country
                        )
                          ? "GST No:"
                          : "Tax No:"}
                      </label>
                      <div className="col-sm-4">
                        {editMode ? (
                          <input
                            type="text"
                            className="form-control"
                            value={editedCompany.gstNo || ""}
                            maxLength={15}
                            onChange={(e) =>
                              setEditedCompany({
                                ...editedCompany,
                                gstNo: e.target.value.toUpperCase(),
                              })
                            }
                          />
                        ) : (
                          <span className="form-control-plaintext">
                            {displayValue(company.gstNo)}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Pan Card - Only show for India */}
                  {(editMode || !editMode) &&
                    isIndia(
                      editMode ? editedCompany.country : company.country
                    ) && (
                      <div className="row mb-3">
                        <label className="col-sm-2 col-form-label">
                          Pan Card:
                        </label>
                        <div className="col-sm-4">
                          {editMode ? (
                            <input
                              type="text"
                              className="form-control"
                              value={editedCompany.panCard || ""}
                              maxLength={15}
                              onChange={(e) =>
                                setEditedCompany({
                                  ...editedCompany,
                                  panCard: e.target.value.toUpperCase(),
                                })
                              }
                            />
                          ) : (
                            <span className="form-control-plaintext">
                              {displayValue(company.panCard)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                  {/* City */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">City:</label>
                    <div className="col-sm-4">
                      {editMode ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editedCompany.city || ""}
                          maxLength={40}
                          onChange={(e) =>
                            setEditedCompany({
                              ...editedCompany,
                              city: e.target.value.toUpperCase(),
                            })
                          }
                        />
                      ) : (
                        <span className="form-control-plaintext">
                          {displayValue(company.city)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* State */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">State:</label>
                    <div className="col-sm-4">
                      {editMode ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editedCompany.state || ""}
                          maxLength={40}
                          onChange={(e) =>
                            setEditedCompany({
                              ...editedCompany,
                              state: e.target.value.toUpperCase(),
                            })
                          }
                        />
                      ) : (
                        <span className="form-control-plaintext">
                          {displayValue(company.state)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Address */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Address:</label>
                    <div className="col-sm-4">
                      {editMode ? (
                        <input
                          type="text"
                          className="form-control"
                          value={editedCompany.address || ""}
                          maxLength={250}
                          onChange={(e) =>
                            setEditedCompany({
                              ...editedCompany,
                              address: e.target.value,
                            })
                          }
                        />
                      ) : (
                        <span className="form-control-plaintext">
                          {displayValue(company.address)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Country */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Country:</label>
                    <div className="col-sm-4">
                      {editMode ? (
                        <select
                          className="form-select"
                          value={editedCompany.country || ""}
                          name="country"
                          onChange={(e) => handleCountryChange(e.target.value)}
                        >
                          <option value="">Select Country</option>
                          {countries.map((country) => (
                            <option
                              key={country.countryCode}
                              value={country.countryCode}
                            >
                              {country.flag && (
                                <img
                                  src={country.flag}
                                  alt={country.country}
                                  style={{
                                    width: "20px",
                                    height: "15px",
                                    marginRight: "8px",
                                    verticalAlign: "middle",
                                  }}
                                />
                              )}
                              {country.country} - {country.countryCode}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="form-control-plaintext">
                          {getCountryName(company.country)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Currency */}
                  {/* Currency */}
                  <div className="row mb-3">
                    <label className="col-sm-2 col-form-label">Currency:</label>
                    <div className="col-sm-4">
                      {editMode ? (
                        <select
                          className="form-select"
                          value={editedCompany.currency || ""}
                          onChange={(e) => handleCurrencyChange(e.target.value)}
                        >
                          <option value="">Select Currency</option>
                          {countries.map((country) => (
                            <option
                              key={country.countryCode}
                              value={country.code}
                            >
                              {country.flag && (
                                <img
                                  src={country.flag}
                                  alt={country.country}
                                  style={{
                                    width: "20px",
                                    height: "15px",
                                    marginRight: "8px",
                                    verticalAlign: "middle",
                                  }}
                                />
                              )}
                              {country.country} - {country.code}
                              {country.currency && ` (${country.currency})`}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className="form-control-plaintext">
                          {displayValue(company.currency)}
                          {company.currency && (
                            <span
                              style={{ marginLeft: "8px", color: "#6c757d" }}
                            ></span>
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="d-flex justify-content-center">
                    {!editMode ? (
                      <button
                        className="btn btn-primary px-4"
                        onClick={startEdit}
                      >
                        <i className="ri-pencil-line me-2"></i>Edit Profile
                      </button>
                    ) : (
                      <>
                        <button
                          className="btn btn-success me-2 px-4"
                          onClick={handleUpdate}
                          disabled={btnLoading}
                        >
                          {btnLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="fas fa-save me-2"></i>Save Changes
                            </>
                          )}
                        </button>
                        <button
                          className="btn btn-danger px-4"
                          onClick={cancelEdit}
                        >
                          <i className="fas fa-times me-2"></i>Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyProfile;
